import ChoicesJS from 'choices.js';
import axios from 'axios';

import Component from '@/scripts/core/Component';

const EMPTY_AJAX_CACHE_KEY = '__default';

const isDefined = (value) => typeof value !== 'undefined' && value !== null;

class Choices extends Component {
  onInit() {
    if (!this.$refs.select) return;

    this.onSearch = this.onSearch.bind(this);
    this.onInput = this.onInput.bind(this);

    const isDisabled = this.$refs.select.getAttribute('disabled') !== null;

    this.ajaxCache = {};
    this.ajax = this.$props.ajax ? ({
      url: '',
      initial: true,
      search: true,
      cache: false,
      queryParam: 'search',
      delay: 200,
      ...this.$props.ajax,
    }) : false;

    // Fix placeholder for select-one
    // TODO: remove when library get fix
    // https://github.com/jshjohnson/Choices/issues/304
    // https://github.com/jshjohnson/Choices/issues/301
    // https://github.com/jshjohnson/Choices/pull/294
    // https://github.com/jshjohnson/Choices/pull/269
    // https://github.com/jshjohnson/Choices/issues/217
    this.selectOnePlaceholder = this.$props.placeholderValue;
    let selectOneInitialValue = '';
    const isSelectOne = this.$refs.select.type === 'select-one';

    if (isSelectOne) {
      const options = this.$refs.select.querySelectorAll('option');
      if (options.length) {
        Array.prototype.forEach.call(options, (option) => {
          if (option.getAttribute('placeholder') !== null) {
            this.selectOnePlaceholder = option.innerText;
            option.parentNode.removeChild(option);
            return;
          }

          if (option.getAttribute('selected') !== null) {
            selectOneInitialValue = option.getAttribute('value');
          }
        });
      }
    }

    // Init choices instance
    this.choices = new ChoicesJS(this.$refs.select, {
      items: this.$props.items || [],
      choices: this.$props.choices || [],
      renderChoiceLimit: this.$props.renderChoiceLimit || -1,
      maxItemCount: this.$props.maxItemCount || -1,
      addItems: true,
      removeItems: isDefined(this.$props.removeItems) ? this.$props.removeItems : true,
      removeItemButton: isDefined(this.$props.removeItems) ? this.$props.removeItems : true,
      editItems: isDefined(this.$props.editItems) ? this.$props.editItems : true,
      duplicateItems: false,
      delimiter: ',',
      paste: true,
      searchEnabled: isDefined(this.$props.searchEnabled) ? this.$props.searchEnabled : true,
      searchChoices: !this.ajax || !this.ajax.search,
      searchFloor: 1,
      searchResultLimit: 10,
      searchFields: ['label', 'value'],
      position: 'auto',
      resetScrollPosition: true,
      regexFilter: null,
      shouldSort: false,
      shouldSortItems: false,
      placeholder: true,
      placeholderValue: this.$props.placeholderValue,
      searchPlaceholderValue: this.$props.searchPlaceholderValue,
      renderSelectedChoices: 'auto',
      noChoicesText: this.$props.noChoicesText || 'Ничего не найдено',
      noResultsText: this.$props.noResultsText || 'Ничего не найдено',
      itemSelectText: '',
      addItemText: (value) => `Press Enter to add <b>"${value}"</b>`,
      maxItemText: (maxItemCount) => `Only ${maxItemCount} values can be added.`,
      itemComparer: (choice, item) => choice === item,
      classNames: {
        containerOuter: 'choices',
        containerInner: 'choices__inner',
        input: 'choices__input',
        inputCloned: 'choices__input_cloned',
        list: 'choices__list',
        listItems: 'choices__list_multiple',
        listSingle: 'choices__list_single',
        listDropdown: 'choices__list_dropdown',
        item: 'choices__item',
        itemSelectable: 'choices__item_selectable',
        itemDisabled: 'choices__item_disabled',
        itemChoice: 'choices__item_choice',
        placeholder: 'choices__placeholder',
        group: 'choices__group',
        groupHeading: 'choices__heading',
        button: 'choices__button',
        activeState: '_active',
        focusState: '_focused',
        openState: '_open',
        disabledState: '_disabled',
        highlightedState: '_highlighted',
        hiddenState: '_hidden',
        flippedState: '_flipped',
        loadingState: '_loading',
        noResults: '_no-results',
        noChoices: '_no-choices',
      },
      // https://github.com/krisk/Fuse#options
      fuseOptions: {
        include: 'score',
      },
    });

    // Fix placeholder for select-one
    if (this.choices.isSelectOneElement && this.selectOnePlaceholder) {
      this.fixSelectOnePlaceholder();
      if (selectOneInitialValue === '' && (!this.$props.items || !this.$props.items.length)) {
        this.choices.setValueByChoice(selectOneInitialValue);
      }
    }

    // Disable if elemen disabled
    if (isDisabled) {
      this.choices.disable();
    }

    // Setup ajax
    if (this.ajax) {
      if (this.ajax.initial) {
        this.fetchChoices();
      }

      if (this.ajax.search) {
        this.choices.passedElement.addEventListener('search', this.onSearch);

        // If search cleared
        // https://github.com/jshjohnson/Choices/issues/74
        if (this.choices.input) {
          this.choices.input.addEventListener('input', this.onInput);
        }
      }
    }
  }

  fetchChoices(query) {
    if (this.ajax.cache) {
      const cachedChoices = this.ajaxCache[query || EMPTY_AJAX_CACHE_KEY];
      if (cachedChoices) {
        this.choices.setChoices(cachedChoices, 'value', 'label', true);
        return;
      }
    }

    this.setLoading(this.choices);
    axios.get(
      this.ajax.url,
      {
        params: {
          [this.ajax.queryParam]: query,
        },
      },
    )
      .then(({ data }) => {
        if (this.ajax.cache) {
          this.ajaxCache[query || EMPTY_AJAX_CACHE_KEY] = data;
        }

        this.choices.setChoices(data, 'value', 'label', true);
        this.fixSelectOnePlaceholder();
      })
      .catch(() => {
        this.setLoadingError();
        // this.fixSelectOnePlaceholder();
      });
  }

  onSearch(e) {
    const query = e.detail.value;

    if (this.ajaxTimer !== null) {
      clearTimeout(this.ajaxTimer);
    }

    this.ajaxTimer = setTimeout(() => {
      this.fetchChoices(query);
    }, this.ajax.delay);
  }

  onInput() {
    if (!this.choices.input.value) {
      this.fetchChoices();
    }
  }

  setNotice(text) {
    const notice = this.choices._getTemplate('notice', text);
    this.choices.choiceList.innerHTML = '';
    this.choices.choiceList.appendChild(notice);
  }

  setLoading() {
    this.setNotice(this.$props.loadingText || 'Загрузка...');
  }

  setLoadingError() {
    this.setNotice(this.$props.loadingError || 'Ошибка');
  }

  fixSelectOnePlaceholder() {
    if (this.choices.isSelectOneElement && this.selectOnePlaceholder) {
      this.choices.setChoices([{ label: this.selectOnePlaceholder, value: '', placeholder: true }], 'value', 'label', false);
      if (this.choices.currentState.choices.length === 1) {
        this.setNotice(this.$props.noResultsText || 'Ничего не найдено');
      }
    }
  }

  onDestroy() {
    if (this.choices) {
      this.choices.passedElement.removeEventListener('search', this.onSearch);
      this.choices.input.removeEventListener('input', this.onInput);
      this.choices.destroy();
      this.choices = null;
    }
  }
}

export default Choices;
